<template>
  <div
    v-if="isVisible && (unlockedRewardsAreVisible || genericOffersAreVisible)"
    class="cf-flex cf-flex--column cf-flex--gap-16"
  >
    <!-- Unlocked Offer Thresholds -->
    <template v-if="unlockedRewardsAreVisible">
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
        <h3 class="cf-title cf-title--3">
          Unlocked Rewards
        </h3>

        <button
          v-if="shouldShowAllUnlockedRewards || hiddenUnlockedThresholdsCount > 0"
          type="button"
          class="cf-flex cf-flex--align-center cf-flex--gap-12 cf-cursor-pointer"
          @click="toggleShowAllUnlockedRewards"
        >
          <template v-if="shouldShowAllUnlockedRewards">
            <span class="cf-subtitle cf-subtitle--2">
              Show Less
            </span>

            <span class="cf-icon cf-icon--smedium far fa-angle-down" />
          </template>

          <template v-else>
            <span class="cf-subtitle cf-subtitle--2">
              {{ hiddenUnlockedThresholdsCount }} more unlocked reward
              <template v-if="hiddenUnlockedThresholdsCount > 1">s</template>
            </span>

            <span class="cf-icon cf-icon--smedium far fa-angle-right" />
          </template>
        </button>
      </div>

      <transition-group
        tag="ul"
        name="list-slide"
        class="cf-flex cf-flex--column cf-flex--gap-12"
      >
        <li
          v-for="threshold in visibleUnlockedThresholds"
          :key="threshold.publicId"
          class="cf-card cf-card--clickable cf-grid cf-grid--2-grid-column-left-large cf-flex--gap-16 cf-cursor-pointer cf-mobile-column"
          @click="viewOfferDetailsClickHandler(threshold.rewardOffer)"
        >
          <div
            v-if="threshold.rewardOffer.couponImageUrl"
            class="cf-card--banners"
          >
            <div class="cf-card--banner cf-card--background-brand-primary">
              {{ threshold.cost }}
              {{ loyalty.currency.pluralShortName }}
            </div>
          </div>
          <div class="cf-flex cf-flex--row cf-flex--grow-2 cf-flex--gap-12">
            <img
              v-if="threshold.rewardOffer.couponImageUrl"
              class="cf-image cf-image--thumbnail"
              :src="threshold.rewardOffer.couponImageUrl"
              onerror="this.src='/images/image-not-found.png';"
            >

            <cf-offer-point-display
              v-else
              :cost="threshold.cost"
              :currency="loyalty.currency.pluralShortNameUpperCase"
            />

            <div class="cf-flex cf-flex--column cf-flex--align-start cf-flex--justify-center cf-flex--gap-8">
              <v-clamp
                v-if="threshold.rewardOffer.title"
                tag="p"
                class="cf-title cf-title--4"
                autoresize
                :max-lines="threshold.rewardOffer.description ? 2 : 3"
              >
                {{ threshold.rewardOffer.title }}
              </v-clamp>

              <v-clamp
                v-if="threshold.rewardOffer.description"
                tag="p"
                class="cf-subtitle cf-subtitle--3"
                autoresize
                :max-lines="2"
              >
                {{ threshold.rewardOffer.description }}
              </v-clamp>
            </div>
          </div>

          <div class="cf-flex cf-flex--row cf-flex--grow-1 cf-flex--align-center cf-flex--justify-end">
            <button
              type="button"
              :class="['cf-button cf-button--primary cf-button--md cf-button--mobile-fill cf-align-self-center', {
                'cf-button--loading': selectedOfferId === threshold.rewardOffer.loyaltyConversionThresholdPublicId
              }]"
              :disabled="disableRedeemButton"
              @click.stop="redeemOfferClickHandler(threshold.rewardOffer)"
            >
              {{ content.Loyalty.metadata.buttonCta }}
            </button>
          </div>
        </li>
      </transition-group>
    </template>

    <!-- Redemeemable Offer Allocations -->
    <template v-if="genericOffersAreVisible">
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
        <h3 class="cf-title cf-title--3">
          {{ content.OrderSummary.metadata.rewards }}
        </h3>

        <button
          v-if="offers.allRedeemableOffers.length > 1"
          type="button"
          class="cf-flex cf-flex--align-center cf-flex--gap-12 cf-cursor-pointer"
          @click="toggleRedeemableOffersHandler"
        >
          <template v-if="areAllRedeemableOffersDisplayed">
            <span class="cf-subtitle cf-subtitle--2">
              Show Less
            </span>

            <span class="cf-icon cf-icon--smedium far fa-angle-down" />
          </template>

          <template v-else>
            <span class="cf-subtitle cf-subtitle--2">
              View more...
            </span>

            <span class="cf-icon cf-icon--smedium far fa-angle-right" />
          </template>
        </button>
      </div>

      <transition-group
        tag="ul"
        name="list-slide"
        class="cf-flex cf-flex--column cf-flex--gap-12"
      >
        <li
          v-for="offer in visibleRedeemableOffers"
          :key="offer.offerAllocationPublicId"
          class="cf-card cf-card--clickable cf-grid cf-grid--2-grid-column-left-large cf-flex--gap-16 cf-cursor-pointer cf-mobile-column"
          @click="viewOfferDetailsClickHandler(offer)"
        >
          <div class="cf-flex cf-flex--row cf-flex--grow-2 cf-flex--gap-12">
            <img
              v-if="offer.couponImageUrl"
              class="cf-image cf-image--thumbnail"
              :src="offer.couponImageUrl"
              alt="coupon image"
              onerror="this.src='/images/image-not-found.png';"
            >

            <div class="cf-flex cf-flex--column cf-flex--align-start cf-flex--justify-center cf-flex--gap-8">
              <h4 class="cf-title cf-title--4 cf-no-wrap-ellipsis-multi-line">
                {{ offer.title }}
              </h4>

              <p
                v-if="offer.endDate"
                class="cf-subtitle cf-subtitle--3"
              >
                Expires {{ $moment(offer.endDate, 'M/D/YY') }}
              </p>
            </div>
          </div>

          <div class="cf-flex cf-flex--row cf-flex--grow-1 cf-flex--align-center cf-flex--justify-end">
            <button
              type="button"
              class="cf-button cf-button--primary cf-button--md cf-button--mobile-fill cf-align-self-center"
              :class="{
                'cf-button--loading': selectedOfferId === offer.offerAllocationPublicId
              }"
              :disabled="disableRedeemButton"
              @click.stop="redeemOfferClickHandler(offer)"
            >
              {{ content.Loyalty.metadata.buttonCta }}
            </button>
          </div>
        </li>
      </transition-group>
    </template>

    <cf-verify-account-cta v-if="appliedOffers.length === 0" />
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import events from '@/services/events';
  import errorHandlers from '@/services/error-handlers';

  import CfVerifyAccountCta from '@/view/components/globals/cf-verify-account-cta/index.vue';
  import CfOfferPointDisplay from '@/view/components/globals/cf-offer-point-display/index.vue';


  export default {
    name: 'CfCartOffers',

    components: {
      CfVerifyAccountCta,
      CfOfferPointDisplay
    },

    data() {
      return {
        selectedOfferId: null,
        shouldShowAllUnlockedRewards: false,
        areAllRedeemableOffersDisplayed: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('offers', ['loyalty', 'offers']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['currentUser']),

      isVisible() {
        return !!this.currentUser.accountId && this.order.orderAttributes.canAddAccountOffer;
      },

      appliedOffers() {
        return this.order.offers.filter(o => o.applicationStatus === 'Applied');
      },

      disableRedeemButton() {
        return !!this.selectedOfferId || !this.currentUser.allowOfferRedemption || this.appliedOffers.length > 0;
      },

      genericOffersAreVisible() {
        return this.offers.allRedeemableOffers.length > 0;
      },

      hiddenUnlockedThresholdsCount() {
        return this.loyalty.unlockedThresholds.length - this.visibleUnlockedThresholds.length;
      },

      unlockedRewardsAreVisible() {
        return this.loyalty.isMultiTiered && this.loyalty.unlockedThresholds.length > 0;
      },

      visibleUnlockedThresholds() {
        if (this.shouldShowAllUnlockedRewards) {
          return this.loyalty.unlockedThresholds;
        }

        if (this.loyalty.appliedThresholds.length > 0) {
          return this.loyalty.appliedThresholds;
        }

        return this.loyalty.bestUnlockedThreshold ? [this.loyalty.bestUnlockedThreshold] : [];
      },

      visibleRedeemableOffers() {
        return this.areAllRedeemableOffersDisplayed ? this.offers.allRedeemableOffers : [this.offers.allRedeemableOffers[0]];
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      async redeemOfferClickHandler(offer) {
        try {
          this.selectedOfferId = offer.loyaltyConversionThresholdPublicId || offer.offerAllocationPublicId;

          await this.$store.dispatch('orders/redeemOffer', {
            loyaltyConversionThresholdPublicId: offer.loyaltyConversionThresholdPublicId,
            offerAllocationPublicId: offer.offerAllocationPublicId
          });

          this.$closeModal('offerDetails');

          events.emit('notification', {
            type: 'success',
            message: 'Your offer was applied.'
          });

          const eventData = { offer };

          this.$googleEventTracking.postEvent('select_promotion', eventData);

          // this.$triggerGoogleTrackingEvent('ApplyOffer', 'Loyalty');

          this.$triggerNewRelicEvent('ApplyOffer', 'Information', 'Applied Offer');
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.selectedOfferId = null;
        }
      },

      createdHandler() {
        this.loadOffers();
      },

      loadOffers() {
        this.$store.dispatch('offers/getOffers', {
          silent: true
        });
      },

      toggleShowAllUnlockedRewards() {
        this.shouldShowAllUnlockedRewards = !this.shouldShowAllUnlockedRewards;
      },

      toggleRedeemableOffersHandler() {
        this.areAllRedeemableOffersDisplayed = !this.areAllRedeemableOffersDisplayed;
      },

      viewOfferDetailsClickHandler(offer) {
        this.$openModal('offerDetails', { offer });

        const eventData = { offer };

        this.$googleEventTracking.postEvent('view_promotion', eventData);
      }
    }
  };
</script>
